import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Input, Select, Tag, message } from 'antd';
import { Circle, CircleMarker, GeoJSON, Marker, Polygon, Polyline, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import pointOnFeature from '@turf/point-on-feature';
import { BaseMap } from '../../components/Map';
import axios from '../../util/Api';
import {
  CATEGORY_COLORS,
  DRAW_MODE,
  DEFAULT_STYLE,
  buildFenceFeatureCollection,
  buildShapeTooltipHtml,
  circlePolygonFeature,
  computeShapeMetrics,
  createLocalId,
  extractErrorMessage,
  getInteractiveLayerStyle,
  getPrimaryPolygonRingLatLngs,
  getResourcePermissions,
  getUserFromLocalStorage,
  getUserOrganisationMeta,
  parseLatLngInput,
  polygonFromLatLngs,
  polygonHasSelfIntersections,
  rectangleLatLngs,
  resolveShapeType,
  stopLeafletEventPropagation,
} from '../../util/drawingUtils';
import { TILE_LAYERS, getTileLayerAttribution } from '../../util/mapTiles';
import CategoryPanel from './CategoryPanel';
import DrawPanel from './DrawPanel';
import RefImagePanel from './RefImagePanel';
import { isTiff, loadTiff } from './tiffUtils';
import './style.css';

const { Option } = Select;

const INDIA_CENTER = [20.5937, 78.9629];
const DEFAULT_MAP_ZOOM = 5;
const FOCUS_MAP_ZOOM = 15;
const POINT_OF_INTEREST_RESOURCE = 'PointOfInterest';
const CLOSE_POLYGON_PX_THRESHOLD = 12;

// Corner marker icon — orange drag handle
const CORNER_ICON = L.divIcon({
  className: 'poi-corner-handle',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function getFeatureCenter(feature) {
  try {
    if (!feature || feature.type !== 'Feature' || !feature.geometry) return null;
    const centered = pointOnFeature(feature);
    const coords = centered && centered.geometry && centered.geometry.coordinates;
    if (Array.isArray(coords) && coords.length >= 2) return [Number(coords[1]), Number(coords[0])];
  } catch (e) { /* fallback */ }
  try {
    const geometry = feature && feature.geometry;
    if (!geometry || geometry.type !== 'Polygon' || !geometry.coordinates || !geometry.coordinates.length) return null;
    const ring = geometry.coordinates[0];
    if (!ring || !ring.length) return null;
    let sumLat = 0, sumLng = 0, count = 0;
    ring.forEach((c) => { if (Array.isArray(c) && c.length >= 2) { sumLng += Number(c[0]); sumLat += Number(c[1]); count += 1; } });
    if (!count) return null;
    return [sumLat / count, sumLng / count];
  } catch (e) { return null; }
}

export default function PoiManager({ authUser }) {
  const resolvedUser = authUser || getUserFromLocalStorage();
  const isSuperAdmin = !!(resolvedUser && resolvedUser.superAdmin);
  const loggedUserOrganisation = useMemo(() => getUserOrganisationMeta(resolvedUser), [resolvedUser]);

  const poiPermissions = useMemo(() => {
    if (isSuperAdmin) return ['c', 'r', 'u', 'd'];
    return getResourcePermissions((resolvedUser && resolvedUser.roles) || [], POINT_OF_INTEREST_RESOURCE);
  }, [isSuperAdmin, resolvedUser]);

  const canRead = isSuperAdmin || poiPermissions.includes('r');
  const canCreate = isSuperAdmin || poiPermissions.includes('c');
  const canUpdate = isSuperAdmin || poiPermissions.includes('u');
  const canDelete = isSuperAdmin || poiPermissions.includes('d');
  const hasAccess = isSuperAdmin || poiPermissions.length > 0;

  // ── Map state ──────────────────────────────────────────────────────────────
  const mapRef = useRef(null);
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [mapCenter, setMapCenter] = useState(INDIA_CENTER);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [tileLayerType, setTileLayerType] = useState('current');
  const [mapReady, setMapReady] = useState(0);

  // ── Organisation selector ──────────────────────────────────────────────────
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisations, setSelectedOrganisations] = useState([]);

  // ── Saved POIs ─────────────────────────────────────────────────────────────
  const [allPois, setAllPois] = useState([]);
  const [loadingPois, setLoadingPois] = useState(false);

  // ── Category visibility ────────────────────────────────────────────────────
  const [hiddenCategories, setHiddenCategories] = useState([]);

  // ── Drawing state ──────────────────────────────────────────────────────────
  const [drawMode, setDrawMode] = useState(DRAW_MODE.NONE);
  const [workingPoints, setWorkingPoints] = useState([]);
  const [previewPoint, setPreviewPoint] = useState(null);
  const [rectangleStart, setRectangleStart] = useState(null);
  const [rectangleCurrent, setRectangleCurrent] = useState(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadiusMeters, setCircleRadiusMeters] = useState(0);

  // ── Draft / editor ─────────────────────────────────────────────────────────
  const [draftFeature, setDraftFeature] = useState(null);
  const [draftShapeType, setDraftShapeType] = useState(DRAW_MODE.POLYGON);
  // Editable canonical geometry for circle / rectangle drafts (drives the
  // editable inputs in DrawPanel). circle: { center:[lat,lng], radius };
  // rectangle: { c1:[lat,lng], c2:[lat,lng] } (two opposite corners).
  const [draftCircle, setDraftCircle] = useState(null);
  const [draftRectangle, setDraftRectangle] = useState(null);
  const [shapeName, setShapeName] = useState('');
  const [shapeCategory, setShapeCategory] = useState('other');
  const [shapeColor, setShapeColor] = useState(DEFAULT_STYLE.color);
  const [fillOpacity, setFillOpacity] = useState(DEFAULT_STYLE.fillOpacity);
  const [strokeWeight, setStrokeWeight] = useState(DEFAULT_STYLE.weight);
  const [editingExistingFence, setEditingExistingFence] = useState(null);
  const [selectedExistingFenceId, setSelectedExistingFenceId] = useState(null);
  const [pendingFences, setPendingFences] = useState([]);
  const [savingFence, setSavingFence] = useState(false);
  const [savingAllPending, setSavingAllPending] = useState(false);

  // ── Reference image overlay ────────────────────────────────────────────────
  // corners: [NW, NE, SE, SW]  each = [lat, lng]
  const [refImage, setRefImage] = useState(null); // { objectUrl, naturalWidth, naturalHeight }
  const [imageOpacity, setImageOpacity] = useState(0.5);
  const [refCorners, setRefCorners] = useState(null); // [NW, NE, SE, SW] latLng pairs
  const [tiffLoading, setTiffLoading] = useState(false);
  const refOverlayRef = useRef(null);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const orgId = loggedUserOrganisation.id;
  const activeOrgId = isSuperAdmin ? (selectedOrganisations[0] || null) : orgId;

  const visiblePois = useMemo(() => {
    return (allPois || []).filter((poi) => {
      const cat = (poi.category && CATEGORY_COLORS[poi.category]) ? poi.category : 'other';
      return !hiddenCategories.includes(cat);
    });
  }, [allPois, hiddenCategories]);

  const savedFenceFeatureCollection = useMemo(() => buildFenceFeatureCollection(visiblePois, '_id'), [visiblePois]);
  const savedFenceById = useMemo(() => {
    const map = {};
    (allPois || []).forEach((p) => { if (p && p._id) map[String(p._id)] = p; });
    return map;
  }, [allPois]);

  const pendingFenceFeatureCollection = useMemo(() => buildFenceFeatureCollection(pendingFences, 'localId'), [pendingFences]);
  const pendingFenceById = useMemo(() => {
    const map = {};
    (pendingFences || []).forEach((p) => { map[p.localId] = p; });
    return map;
  }, [pendingFences]);

  const styledDraftFeature = useMemo(() => {
    if (!draftFeature) return null;
    return {
      ...draftFeature,
      properties: {
        ...(draftFeature.properties || {}),
        name: shapeName,
        style: { color: shapeColor, fillColor: shapeColor, fillOpacity, weight: strokeWeight },
      },
    };
  }, [draftFeature, shapeName, shapeColor, fillOpacity, strokeWeight]);

  // react-leaflet v2 <GeoJSON> ignores `data` changes after mount, so the draft
  // layer must be re-keyed whenever the geometry changes (e.g. edited via the
  // circle/rectangle inputs) to force a remount and redraw.
  const draftFeatureKey = useMemo(() => {
    if (!draftFeature || !draftFeature.geometry) return 'draft-none';
    try { return `draft-${JSON.stringify(draftFeature.geometry.coordinates)}`; }
    catch (e) { return `draft-${Date.now()}`; }
  }, [draftFeature]);

  const previewRectangle = useMemo(() => rectangleLatLngs(rectangleStart, rectangleCurrent), [rectangleStart, rectangleCurrent]);
  const polygonLivePoints = useMemo(() => {
    if (drawMode !== DRAW_MODE.POLYGON) return [];
    return (previewPoint && workingPoints.length) ? workingPoints.concat([previewPoint]) : workingPoints;
  }, [drawMode, previewPoint, workingPoints]);

  const draftShapeMetrics = useMemo(() => styledDraftFeature ? computeShapeMetrics(styledDraftFeature, draftShapeType) : null, [styledDraftFeature, draftShapeType]);

  const liveShapeMetrics = useMemo(() => {
    if (drawMode === DRAW_MODE.POLYGON && polygonLivePoints.length >= 3) {
      return computeShapeMetrics(polygonFromLatLngs(polygonLivePoints), DRAW_MODE.POLYGON);
    }
    if (drawMode === DRAW_MODE.RECTANGLE && previewRectangle.length >= 4) {
      return computeShapeMetrics(polygonFromLatLngs(previewRectangle), DRAW_MODE.RECTANGLE);
    }
    if (drawMode === DRAW_MODE.CIRCLE && circleCenter && circleRadiusMeters > 0) {
      return computeShapeMetrics(circlePolygonFeature(circleCenter, circleRadiusMeters, 72), DRAW_MODE.CIRCLE, { radiusOverrideMeters: circleRadiusMeters });
    }
    return null;
  }, [drawMode, polygonLivePoints, previewRectangle, circleCenter, circleRadiusMeters]);

  const activeMetricsShapeType = drawMode !== DRAW_MODE.NONE ? drawMode : draftShapeType;
  const activeMetrics = drawMode !== DRAW_MODE.NONE ? liveShapeMetrics : draftShapeMetrics;

  const savedFenceLayerKey = useMemo(() => {
    const latest = (allPois || []).reduce((t, p) => Math.max(t, Number(new Date(p && p.updatedAt)) || 0), 0);
    return `poi-saved-${(allPois || []).length}-${latest}`;
  }, [allPois]);

  const pendingFenceLayerKey = useMemo(() => `poi-pending-${(pendingFences || []).map((p) => p.localId).join('|')}`, [pendingFences]);

  // ── API ──────────────────────────────────────────────────────────────────────
  const fetchOrganisations = useCallback(async () => {
    try {
      const res = await axios.get('/organisation/distinct');
      setOrganisations(Array.isArray(res.data) ? res.data : []);
    } catch (e) { message.error('Unable to load organisations'); }
  }, []);

  const fetchPois = useCallback(async (orgIds) => {
    const ids = (orgIds || []).filter(Boolean);
    if (!canRead || !ids.length) { setAllPois([]); return; }
    try {
      setLoadingPois(true);
      const results = await Promise.all(
        ids.map((id) => axios.get(`/geo-fence/superadmin?limit=2000&offset=0&sortField=updatedAt&sortOrder=-1&organisation=${encodeURIComponent(id)}`))
      );
      const merged = [];
      results.forEach((res) => {
        const data = Array.isArray(res.data && res.data.data) ? res.data.data : [];
        data.forEach((p) => merged.push(p));
      });
      merged.sort((a, b) => (Number(new Date(b && b.updatedAt)) || 0) - (Number(new Date(a && a.updatedAt)) || 0));
      setAllPois(merged);
      const poi = merged.find((p) => getFeatureCenter(p && p.feature));
      if (poi) { const c = getFeatureCenter(poi.feature); if (c) flyToLocation(c, FOCUS_MAP_ZOOM); }
    } catch (e) {
      message.error('Unable to load POIs');
      setAllPois([]);
    } finally {
      setLoadingPois(false);
    }
  }, [canRead]);

  useEffect(() => {
    if (!hasAccess) return;
    if (isSuperAdmin) { fetchOrganisations(); }
    else { if (orgId) setSelectedOrganisations([orgId]); }
  }, [hasAccess, isSuperAdmin, orgId]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchPois(selectedOrganisations);
  }, [selectedOrganisations, hasAccess]);

  // ── Map helpers ──────────────────────────────────────────────────────────────
  const getLeafletMap = () => mapRef.current && mapRef.current.leafletElement;
  const flyToLocation = (location, zoom = FOCUS_MAP_ZOOM) => {
    if (!location || location.length !== 2) return;
    const center = [Number(location[0]), Number(location[1])];
    setMapCenter(center);
    setMapZoom(zoom);
    const map = getLeafletMap();
    if (map) map.flyTo(center, zoom, { animate: true, duration: 0.8 });
  };

  const handleMapReady = useCallback(() => setMapReady((v) => v + 1), []);

  const handleMapZoomEnd = () => {
    const map = getLeafletMap();
    if (map) { setMapZoom(map.getZoom()); const c = map.getCenter(); if (c) setMapCenter([Number(c.lat), Number(c.lng)]); }
  };
  const handleMapMoveEnd = () => {
    const map = getLeafletMap();
    if (map) { const c = map.getCenter(); if (c) setMapCenter([Number(c.lat), Number(c.lng)]); }
  };

  // ── Category toggle ──────────────────────────────────────────────────────────
  const handleToggleCategory = (cat, visible) => {
    setHiddenCategories((prev) => visible ? prev.filter((c) => c !== cat) : [...prev.filter((c) => c !== cat), cat]);
  };

  // ── Drawing ──────────────────────────────────────────────────────────────────
  const resetDrawingState = () => {
    setWorkingPoints([]); setPreviewPoint(null);
    setRectangleStart(null); setRectangleCurrent(null);
    setCircleCenter(null); setCircleRadiusMeters(0);
  };

  const clearGeometryDrafts = () => { setDraftCircle(null); setDraftRectangle(null); };

  const clearCurrentDraft = () => {
    setDraftFeature(null); setDraftShapeType(DRAW_MODE.POLYGON);
    setShapeName(''); setShapeCategory('other');
    setEditingExistingFence(null); setSelectedExistingFenceId(null);
    clearGeometryDrafts();
    resetDrawingState(); setDrawMode(DRAW_MODE.NONE);
  };

  const handleSetMode = (mode) => {
    if (mode !== DRAW_MODE.NONE && !(canCreate || canUpdate)) { message.warning('No permission to draw'); return; }
    resetDrawingState(); setDraftFeature(null); clearGeometryDrafts();
    setDraftShapeType(mode === DRAW_MODE.NONE ? DRAW_MODE.POLYGON : mode);
    if (!editingExistingFence) { setShapeName(''); setEditingExistingFence(null); setSelectedExistingFenceId(null); }
    setDrawMode(mode);
  };

  const finishPolygon = useCallback((pts) => {
    if (!pts || pts.length < 3) { message.warning('Polygon needs at least 3 points'); return; }
    if (polygonHasSelfIntersections(pts)) {
      message.error({ content: 'Polygon has self-intersecting edges. Undo the last points and redraw to fix.', duration: 6 });
      return;
    }
    setDraftFeature(polygonFromLatLngs(pts));
    setDraftShapeType(DRAW_MODE.POLYGON);
    clearGeometryDrafts();
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
  }, []);

  const finalizeRectangle = (endPoint) => {
    const corners = rectangleLatLngs(rectangleStart, endPoint);
    if (!corners.length) return;
    setDraftFeature(polygonFromLatLngs(corners));
    setDraftShapeType(DRAW_MODE.RECTANGLE);
    setDraftCircle(null);
    setDraftRectangle({ c1: rectangleStart, c2: endPoint });
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
  };

  const finalizeCircle = (edgePoint) => {
    const radius = L.latLng(circleCenter[0], circleCenter[1]).distanceTo(L.latLng(edgePoint[0], edgePoint[1]));
    if (!radius || radius < 5) { message.warning('Circle radius too small'); return; }
    setDraftFeature(circlePolygonFeature(circleCenter, radius, 72));
    setDraftShapeType(DRAW_MODE.CIRCLE);
    setDraftRectangle(null);
    setDraftCircle({ center: circleCenter, radius });
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
  };

  // ── Editable geometry (circle / rectangle inputs in DrawPanel) ────────────────
  // Update the canonical params from the inputs and regenerate the draft feature
  // so the map redraws live. The raw params are always stored (so the field shows
  // exactly what was typed); the feature is only rebuilt once the values are valid.
  const applyCircleGeometry = ({ center, radius }) => {
    setDraftCircle({ center, radius });
    setDraftShapeType(DRAW_MODE.CIRCLE);
    const lat = Number(center && center[0]);
    const lng = Number(center && center[1]);
    const r = Number(radius);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(r) && r > 0) {
      setDraftFeature(circlePolygonFeature([lat, lng], r, 72));
    }
  };

  const applyRectangleGeometry = ({ c1, c2 }) => {
    setDraftRectangle({ c1, c2 });
    setDraftShapeType(DRAW_MODE.RECTANGLE);
    const valid = [c1, c2].every((c) => c && Number.isFinite(Number(c[0])) && Number.isFinite(Number(c[1])));
    if (valid) {
      const corners = rectangleLatLngs(
        [Number(c1[0]), Number(c1[1])],
        [Number(c2[0]), Number(c2[1])],
      );
      if (corners.length) setDraftFeature(polygonFromLatLngs(corners));
    }
  };

  const workingPointsRef = useRef(workingPoints);
  useEffect(() => { workingPointsRef.current = workingPoints; }, [workingPoints]);

  useEffect(() => {
    if (drawMode !== DRAW_MODE.POLYGON) return undefined;
    const handler = (e) => {
      if (e.key !== 'Enter') return;
      const tag = (e.target && e.target.tagName ? String(e.target.tagName).toLowerCase() : '');
      if (e.target.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const pts = workingPointsRef.current;
      if (!pts || pts.length < 3) return;
      e.preventDefault();
      finishPolygon(pts);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [drawMode, finishPolygon]);

  const handleMapClick = useCallback((event) => {
    if (!event || !event.latlng) return;
    const point = [event.latlng.lat, event.latlng.lng];
    if (!(canCreate || canUpdate)) return;
    if (drawMode === DRAW_MODE.POLYGON) {
      setWorkingPoints((prev) => {
        if (prev.length >= 3) {
          const map = getLeafletMap();
          if (map) {
            const firstPx = map.latLngToContainerPoint(L.latLng(prev[0][0], prev[0][1]));
            const clickPx = map.latLngToContainerPoint(L.latLng(point[0], point[1]));
            if (Math.hypot(firstPx.x - clickPx.x, firstPx.y - clickPx.y) <= CLOSE_POLYGON_PX_THRESHOLD) {
              finishPolygon(prev);
              return prev;
            }
          }
        }
        return prev.concat([point]);
      });
      return;
    }
    if (drawMode === DRAW_MODE.RECTANGLE) {
      if (!rectangleStart) { setRectangleStart(point); setRectangleCurrent(point); }
      else { finalizeRectangle(point); }
      return;
    }
    if (drawMode === DRAW_MODE.CIRCLE) {
      if (!circleCenter) { setCircleCenter(point); setCircleRadiusMeters(0); }
      else { finalizeCircle(point); }
    }
  }, [canCreate, canUpdate, drawMode, rectangleStart, circleCenter, finishPolygon]);

  const handleMapDblClick = useCallback((event) => {
    if (drawMode !== DRAW_MODE.POLYGON) return;
    setWorkingPoints((prev) => {
      let pts = prev;
      if (pts.length >= 2) {
        const map = getLeafletMap();
        if (map) {
          const lastPx = map.latLngToContainerPoint(L.latLng(pts[pts.length - 1][0], pts[pts.length - 1][1]));
          const prev2Px = map.latLngToContainerPoint(L.latLng(pts[pts.length - 2][0], pts[pts.length - 2][1]));
          if (Math.hypot(lastPx.x - prev2Px.x, lastPx.y - prev2Px.y) <= CLOSE_POLYGON_PX_THRESHOLD * 2) {
            pts = pts.slice(0, -1);
          }
        }
      }
      finishPolygon(pts);
      return pts;
    });
  }, [drawMode, finishPolygon]);

  const handleMapMouseMove = useCallback((event) => {
    if (!event || !event.latlng) return;
    const point = [event.latlng.lat, event.latlng.lng];
    if (drawMode === DRAW_MODE.POLYGON && workingPoints.length) { setPreviewPoint(point); return; }
    if (drawMode === DRAW_MODE.RECTANGLE && rectangleStart) { setRectangleCurrent(point); return; }
    if (drawMode === DRAW_MODE.CIRCLE && circleCenter) {
      setCircleRadiusMeters(L.latLng(circleCenter[0], circleCenter[1]).distanceTo(L.latLng(point[0], point[1])));
    }
  }, [drawMode, workingPoints.length, rectangleStart, circleCenter]);

  // ── POI save/update/delete ───────────────────────────────────────────────────
  const validateEditor = () => {
    if (!String(shapeName || '').trim()) { message.warning('Name is required'); return false; }
    if (!draftFeature) { message.warning('Draw a shape first'); return false; }
    return true;
  };

  const buildPoiPayload = () => ({
    name: String(shapeName || '').trim(),
    organisation: activeOrgId,
    feature: styledDraftFeature,
    shapeType: resolveShapeType(styledDraftFeature, draftShapeType),
    category: shapeCategory || 'other',
    layer: null,
    color: shapeColor,
    fillColor: shapeColor,
    fillOpacity: Number(fillOpacity),
    weight: Number(strokeWeight),
    isActive: true,
  });

  const handleAddToBatch = () => {
    if (!canCreate) { message.warning('No permission'); return; }
    if (editingExistingFence) { message.warning('Update the existing POI first or clear edit'); return; }
    if (!validateEditor()) return;
    const item = { localId: createLocalId('pending'), name: String(shapeName || '').trim(), feature: styledDraftFeature, shapeType: resolveShapeType(styledDraftFeature, draftShapeType), category: shapeCategory || 'other', color: shapeColor, fillColor: shapeColor, fillOpacity: Number(fillOpacity), weight: Number(strokeWeight), isActive: true };
    setPendingFences((prev) => prev.concat([item]));
    clearCurrentDraft();
    message.success('Added to batch');
  };

  const handleRemovePending = (localId) => {
    setPendingFences((prev) => prev.filter((p) => p.localId !== localId));
  };

  const handleSaveAllPending = async () => {
    if (!canCreate) { message.warning('No permission'); return; }
    if (!activeOrgId) { message.warning('Select an organisation first'); return; }
    if (!pendingFences.length) { message.warning('No pending shapes'); return; }
    try {
      setSavingAllPending(true);
      const items = pendingFences;
      const results = await Promise.allSettled(
        items.map((s) => axios.post('/geo-fence', { name: s.name, organisation: activeOrgId, feature: s.feature, shapeType: s.shapeType, category: s.category, layer: null, color: s.color, fillColor: s.fillColor || s.color, fillOpacity: Number(s.fillOpacity), weight: Number(s.weight), isActive: true }))
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failures = results
        .map((r, i) => ({ r, item: items[i] }))
        .filter((x) => x.r.status === 'rejected');

      if (okCount) message.success(`Saved ${okCount} POI(s)`);

      // Surface the actual server error (e.g. duplicate name) per failed item.
      failures.slice(0, 4).forEach(({ r, item }) => {
        const detail = extractErrorMessage(r.reason) || 'Unable to save';
        message.error({ content: `${item.name || 'POI'}: ${detail}`, duration: 6 });
      });
      if (failures.length > 4) {
        message.error(`…and ${failures.length - 4} more failed`);
      }

      // Keep only the shapes that failed so the user can fix/rename and retry.
      const failedIds = new Set(failures.map((x) => x.item.localId));
      setPendingFences((prev) => prev.filter((p) => failedIds.has(p.localId)));
      await fetchPois(selectedOrganisations);
    } catch (e) { message.error(extractErrorMessage(e) || 'Unable to save'); }
    finally { setSavingAllPending(false); }
  };

  const handleUpdateExisting = async () => {
    if (!canUpdate) { message.warning('No permission'); return; }
    if (!editingExistingFence || !editingExistingFence._id) return;
    if (!validateEditor()) return;
    const payload = buildPoiPayload();
    payload.organisation = editingExistingFence._sourceOrganisation || editingExistingFence.organisation || activeOrgId;
    try {
      setSavingFence(true);
      await axios.put(`/geo-fence/${editingExistingFence._id}`, payload);
      message.success('POI updated');
      clearCurrentDraft();
      await fetchPois(selectedOrganisations);
    } catch (e) { message.error(extractErrorMessage(e) || 'Unable to update'); }
    finally { setSavingFence(false); }
  };

  const handleDelete = async (id) => {
    if (!canDelete) { message.warning('No permission'); return; }
    try {
      await axios.delete(`/geo-fence/${id}`);
      message.success('POI deleted');
      if (editingExistingFence && editingExistingFence._id === id) clearCurrentDraft();
      setAllPois((prev) => prev.filter((p) => p._id !== id));
    } catch (e) { message.error('Unable to delete POI'); }
  };

  // flyTo=false when the shape is already visible (e.g. user clicked it on the map).
  // flyTo=true only when navigating from the list and the shape may be off-screen.
  const loadShapeIntoEditor = (shape, { flyTo = false } = {}) => {
    if (!shape || !shape.feature) return;
    const feature = shape.feature;
    const st = resolveShapeType(feature, shape.shapeType);
    setDraftFeature(feature);
    setDraftShapeType(st);

    // Seed the editable geometry inputs from the loaded shape.
    if (st === DRAW_MODE.CIRCLE) {
      const center = getFeatureCenter(feature);
      const m = computeShapeMetrics(feature, DRAW_MODE.CIRCLE);
      const radius = m && Number(m.radiusMeters);
      setDraftRectangle(null);
      setDraftCircle(center && Number.isFinite(radius) ? { center, radius } : null);
    } else if (st === DRAW_MODE.RECTANGLE) {
      const ring = getPrimaryPolygonRingLatLngs(feature);
      setDraftCircle(null);
      if (ring.length) {
        const lats = ring.map((p) => Number(p[0]));
        const lngs = ring.map((p) => Number(p[1]));
        setDraftRectangle({
          c1: [Math.min(...lats), Math.min(...lngs)],
          c2: [Math.max(...lats), Math.max(...lngs)],
        });
      } else {
        setDraftRectangle(null);
      }
    } else {
      clearGeometryDrafts();
    }
    setShapeName(shape.name || (feature.properties && feature.properties.name) || '');
    setShapeCategory(shape.category || 'other');
    setShapeColor(shape.color || DEFAULT_STYLE.color);
    setFillOpacity(typeof shape.fillOpacity === 'number' ? shape.fillOpacity : DEFAULT_STYLE.fillOpacity);
    setStrokeWeight(typeof shape.weight === 'number' ? shape.weight : DEFAULT_STYLE.weight);
    setEditingExistingFence(shape);
    setSelectedExistingFenceId(shape._id || null);
    setDrawMode(DRAW_MODE.NONE);
    resetDrawingState();
    if (flyTo) {
      const c = getFeatureCenter(feature);
      if (c) flyToLocation(c, FOCUS_MAP_ZOOM);
    }
  };

  // Called from the category list — only fly if the shape center is outside the
  // current map viewport so the user isn't unexpectedly zoomed out.
  const handlePoiSelect = (poi) => {
    if (!poi) return;
    const center = getFeatureCenter(poi.feature);
    const map = getLeafletMap();
    const isInView = center && map && map.getBounds().contains(L.latLng(center[0], center[1]));
    if (canUpdate) {
      loadShapeIntoEditor(poi, { flyTo: !isInView });
    } else {
      setSelectedExistingFenceId(poi._id);
      if (!isInView && center) flyToLocation(center, FOCUS_MAP_ZOOM);
    }
  };

  // ── Map search ───────────────────────────────────────────────────────────────
  const handleMapSearch = async () => {
    const query = String(mapSearchQuery || '').trim();
    if (!query) { message.warning('Enter a place or lat,lng'); return; }
    const direct = parseLatLngInput(query);
    if (direct) { flyToLocation(direct, FOCUS_MAP_ZOOM); return; }
    try {
      setSearchingLocation(true);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const first = Array.isArray(data) && data.length ? data[0] : null;
      if (!first || !first.lat || !first.lon) { message.warning('No location found'); return; }
      flyToLocation([Number(first.lat), Number(first.lon)], FOCUS_MAP_ZOOM);
    } catch (e) { message.error('Location search failed'); }
    finally { setSearchingLocation(false); }
  };

  // ── Reference image overlay ──────────────────────────────────────────────────
  const getMapBoundsAsCorners = () => {
    const map = getLeafletMap();
    if (!map) return null;
    const b = map.getBounds();
    // [NW, NE, SE, SW]
    return [
      [b.getNorth(), b.getWest()],
      [b.getNorth(), b.getEast()],
      [b.getSouth(), b.getEast()],
      [b.getSouth(), b.getWest()],
    ];
  };

  const handleImageUpload = async (file) => {
    if (isTiff(file)) {
      setTiffLoading(true);
      try {
        const { canvas, bounds, width, height } = await loadTiff(file);
        const objectUrl = canvas.toDataURL('image/png');
        setRefImage({ objectUrl, naturalWidth: width, naturalHeight: height });

        if (bounds) {
          // GeoTIFF with detected bounds — auto-place at correct location.
          const [[south, west], [north, east]] = bounds;
          setRefCorners([
            [north, west], // NW
            [north, east], // NE
            [south, east], // SE
            [south, west], // SW
          ]);
          // Fly to the GeoTIFF extent.
          const centerLat = (south + north) / 2;
          const centerLng = (west + east) / 2;
          flyToLocation([centerLat, centerLng], FOCUS_MAP_ZOOM);
          message.success('GeoTIFF loaded — image auto-positioned from embedded georeference.');
        } else {
          setRefCorners(getMapBoundsAsCorners());
          message.info('TIFF loaded. No georeference found — drag the corner handles to align the image.');
        }
      } catch (err) {
        message.error('Unable to read TIFF file. Please check the file is a valid TIFF or GeoTIFF.');
      } finally {
        setTiffLoading(false);
      }
      return;
    }

    // Normal image (PNG / JPG)
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setRefImage({ objectUrl, naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
      setRefCorners(getMapBoundsAsCorners());
    };
    img.src = objectUrl;
  };

  const handleClearImage = () => {
    if (refImage) URL.revokeObjectURL(refImage.objectUrl);
    setRefImage(null);
    setRefCorners(null);
    if (refOverlayRef.current) { refOverlayRef.current.remove(); refOverlayRef.current = null; }
  };

  const handleResetToView = () => {
    const corners = getMapBoundsAsCorners();
    if (corners) setRefCorners(corners);
  };

  const handleCornerDrag = useCallback((cornerIndex, latLng) => {
    setRefCorners((prev) => {
      if (!prev) return prev;
      const next = prev.map((c, i) => (i === cornerIndex ? [latLng.lat, latLng.lng] : c));
      return next;
    });
  }, []);

  // Imperatively manage the ImageOverlay since react-leaflet v2 doesn't support
  // dynamic bounds updates on ImageOverlay easily.
  useEffect(() => {
    if (mapReady === 0) return;
    const map = getLeafletMap();
    if (!map) return;

    if (!refImage || !refCorners) {
      if (refOverlayRef.current) { refOverlayRef.current.remove(); refOverlayRef.current = null; }
      return;
    }

    // Compute rectangular bounds from the 4 corners (min/max lat/lng).
    const lats = refCorners.map((c) => c[0]);
    const lngs = refCorners.map((c) => c[1]);
    const sw = [Math.min(...lats), Math.min(...lngs)];
    const ne = [Math.max(...lats), Math.max(...lngs)];

    if (refOverlayRef.current) {
      refOverlayRef.current.setBounds([sw, ne]);
      refOverlayRef.current.setOpacity(imageOpacity);
    } else {
      const overlay = L.imageOverlay(refImage.objectUrl, [sw, ne], { opacity: imageOpacity, interactive: false, zIndex: 300 });
      overlay.addTo(map);
      refOverlayRef.current = overlay;
    }
  }, [mapReady, refImage, refCorners, imageOpacity]);

  useEffect(() => {
    if (refOverlayRef.current) refOverlayRef.current.setOpacity(imageOpacity);
  }, [imageOpacity]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (refOverlayRef.current) { refOverlayRef.current.remove(); refOverlayRef.current = null; }
    if (refImage) URL.revokeObjectURL(refImage.objectUrl);
  }, []);

  if (!hasAccess) {
    return (
      <Card>
        <Alert type="error" showIcon message="Unauthorized" description="You do not have Point of Interest access." />
      </Card>
    );
  }

  return (
    <div className="poi-manager-page">
      {/* ── Left panel: Draw tools ── */}
      <div className="poi-left-panel">
        <Card bodyStyle={{ padding: 0, height: '100%', overflowY: 'auto' }}>
          <DrawPanel
            drawMode={drawMode}
            workingPoints={workingPoints}
            rectangleStart={rectangleStart}
            circleCenter={circleCenter}
            draftFeature={draftFeature}
            draftShapeType={draftShapeType}
            draftCircle={draftCircle}
            draftRectangle={draftRectangle}
            onApplyCircleGeometry={applyCircleGeometry}
            onApplyRectangleGeometry={applyRectangleGeometry}
            activeMetrics={activeMetrics}
            activeMetricsShapeType={activeMetricsShapeType}
            shapeName={shapeName}
            setShapeName={setShapeName}
            shapeColor={shapeColor}
            setShapeColor={setShapeColor}
            shapeCategory={shapeCategory}
            setShapeCategory={setShapeCategory}
            fillOpacity={fillOpacity}
            setFillOpacity={setFillOpacity}
            strokeWeight={strokeWeight}
            setStrokeWeight={setStrokeWeight}
            editingExistingFence={editingExistingFence}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onSetMode={handleSetMode}
            onCancelDraw={() => { resetDrawingState(); setDrawMode(DRAW_MODE.NONE); }}
            onFinishPolygon={() => finishPolygon(workingPoints)}
            onUndoPoint={() => setWorkingPoints((prev) => prev.slice(0, -1))}
            onAddToBatch={handleAddToBatch}
            onUpdateExisting={handleUpdateExisting}
            onClearDraft={clearCurrentDraft}
            onDelete={handleDelete}
            onSaveAllPending={handleSaveAllPending}
            onRemovePending={handleRemovePending}
            pendingFences={pendingFences}
            savingFence={savingFence}
            savingAllPending={savingAllPending}
          />
        </Card>
      </div>

      {/* ── Center: Map ── */}
      <div className="poi-map-col">
        <div className="poi-map-wrap">
          {/* Floating map toolbar */}
          <div className="poi-map-toolbar">
            <div className="poi-map-search-row">
              <Input
                size="small"
                value={mapSearchQuery}
                onChange={(e) => setMapSearchQuery(e.target.value)}
                onPressEnter={handleMapSearch}
                placeholder="Search place or lat,lng"
                style={{ width: 180 }}
              />
              <Button size="small" type="primary" onClick={handleMapSearch} loading={searchingLocation}>Go</Button>
            </div>
            <div className="poi-tile-switcher">
              {Object.keys(TILE_LAYERS).map((key) => (
                <button
                  key={key}
                  className={`poi-tile-btn${tileLayerType === key ? ' poi-tile-btn-active' : ''}`}
                  onClick={() => setTileLayerType(key)}
                >
                  {TILE_LAYERS[key].label}
                </button>
              ))}
            </div>
          </div>

          <BaseMap
            mapRef={mapRef}
            center={mapCenter}
            zoom={mapZoom}
            onZoomEnd={handleMapZoomEnd}
            onMoveend={handleMapMoveEnd}
            onClick={handleMapClick}
            onDblclick={handleMapDblClick}
            onMousemove={handleMapMouseMove}
            whenReady={handleMapReady}
            doubleClickZoom={false}
            attributionControl={true}
            preferCanvas
            showIndiaGeoJson={false}
            showMaaGeoJson={false}
            showGeoFenceOverlay={false}
            tileLayerUrl={TILE_LAYERS[tileLayerType].url}
            tileLayerMaxZoom={TILE_LAYERS[tileLayerType].maxZoom}
            tileLayerSubdomains={TILE_LAYERS[tileLayerType].subdomains || undefined}
            tileLayerAttribution={getTileLayerAttribution(tileLayerType)}
            style={{ height: '100%', width: '100%' }}
          >
            {/* Hybrid label overlay */}
            {TILE_LAYERS[tileLayerType].overlay && (
              <TileLayer
                url={TILE_LAYERS[tileLayerType].overlay}
                subdomains={TILE_LAYERS[tileLayerType].overlaySubdomains || ['a', 'b', 'c']}
                opacity={TILE_LAYERS[tileLayerType].overlayOpacity || 0.4}
                maxZoom={22}
                attribution={TILE_LAYERS[tileLayerType].overlayAttribution}
              />
            )}

            {/* Reference image corner drag handles */}
            {refCorners && refCorners.map((corner, i) => (
              <Marker
                key={`ref-corner-${i}`}
                position={corner}
                icon={CORNER_ICON}
                draggable
                onDragend={(e) => handleCornerDrag(i, e.target.getLatLng())}
              />
            ))}

            {/* Saved POIs */}
            {savedFenceFeatureCollection && (
              <GeoJSON
                key={savedFenceLayerKey}
                data={savedFenceFeatureCollection}
                style={(feature) => {
                  const fenceId = feature && feature.properties && feature.properties._fenceId;
                  const fence = savedFenceById[String(fenceId)] || {};
                  return getInteractiveLayerStyle(feature, {
                    ...fence,
                    color: fence.color || CATEGORY_COLORS[fence.category] || DEFAULT_STYLE.color,
                  }, {
                    selected: String(selectedExistingFenceId || '') === String(fenceId || '')
                      || String((editingExistingFence && editingExistingFence._id) || '') === String(fenceId || ''),
                  });
                }}
                onEachFeature={(feature, layer) => {
                  const fenceId = feature && feature.properties && feature.properties._fenceId;
                  const fence = savedFenceById[String(fenceId)];
                  if (fence) {
                    const label = (feature.properties && feature.properties.name) || fence.name;
                    const st = resolveShapeType(feature, fence.shapeType);
                    const metrics = computeShapeMetrics(feature, st);
                    if (label || metrics) layer.bindTooltip(buildShapeTooltipHtml(label, metrics, st), { sticky: true, direction: 'top' });
                    layer.on('click', (e) => {
                      stopLeafletEventPropagation(e);
                      if (canUpdate || canDelete) loadShapeIntoEditor(fence);
                      else setSelectedExistingFenceId(fence._id);
                    });
                  }
                }}
              />
            )}

            {/* Pending shapes */}
            {pendingFenceFeatureCollection && (
              <GeoJSON
                key={pendingFenceLayerKey}
                data={pendingFenceFeatureCollection}
                style={(feature) => {
                  const fenceId = feature && feature.properties && feature.properties._fenceId;
                  const fence = pendingFenceById[String(fenceId)] || {};
                  return getInteractiveLayerStyle(feature, fence, { pending: true });
                }}
                onEachFeature={(feature, layer) => {
                  const fenceId = feature && feature.properties && feature.properties._fenceId;
                  const fence = pendingFenceById[String(fenceId)];
                  if (fence) {
                    const st = resolveShapeType(feature, fence.shapeType);
                    layer.bindTooltip(buildShapeTooltipHtml(fence.name || 'Pending POI', computeShapeMetrics(feature, st), st), { sticky: true, direction: 'top' });
                  }
                }}
              />
            )}

            {/* Live polygon preview */}
            {drawMode === DRAW_MODE.POLYGON && workingPoints.length > 0 && (() => {
              const livePoints = previewPoint ? workingPoints.concat([previewPoint]) : workingPoints;
              const canFill = livePoints.length >= 3;
              return (
                <>
                  {canFill ? (
                    <Polygon positions={livePoints} color={shapeColor} fillColor={shapeColor} fillOpacity={fillOpacity * 0.7} weight={strokeWeight} dashArray="6 3" />
                  ) : (
                    <Polyline positions={livePoints} color={shapeColor} weight={strokeWeight} dashArray="6 3" />
                  )}
                  {previewPoint && workingPoints.length > 0 && (
                    <Polyline positions={[workingPoints[workingPoints.length - 1], previewPoint]} color={shapeColor} weight={strokeWeight} dashArray="4 4" opacity={0.6} />
                  )}
                  {workingPoints.map((pt, i) => (
                    <CircleMarker
                      key={`v-${i}`}
                      center={pt}
                      radius={i === 0 && workingPoints.length >= 3 ? 7 : 5}
                      color="#fff" weight={2}
                      fillColor={i === 0 && workingPoints.length >= 3 ? '#52c41a' : shapeColor}
                      fillOpacity={1}
                    />
                  ))}
                </>
              );
            })()}

            {/* Live rectangle preview */}
            {drawMode === DRAW_MODE.RECTANGLE && previewRectangle.length > 0 && (
              <Polygon positions={previewRectangle} color={shapeColor} fillColor={shapeColor} fillOpacity={fillOpacity} weight={strokeWeight} />
            )}

            {/* Live circle preview */}
            {drawMode === DRAW_MODE.CIRCLE && circleCenter && circleRadiusMeters > 0 && (
              <Circle center={circleCenter} radius={circleRadiusMeters} color={shapeColor} fillColor={shapeColor} fillOpacity={fillOpacity} weight={strokeWeight} />
            )}

            {/* Draft feature */}
            {styledDraftFeature && (
              <GeoJSON
                key={draftFeatureKey}
                data={styledDraftFeature}
                style={(feature) => getInteractiveLayerStyle(feature, {}, { selected: true })}
                onEachFeature={(feature, layer) => {
                  const st = resolveShapeType(feature, draftShapeType);
                  layer.bindTooltip(buildShapeTooltipHtml(shapeName || 'Draft POI', computeShapeMetrics(feature, st), st), { sticky: true, direction: 'top' });
                  layer.on('click', (e) => stopLeafletEventPropagation(e));
                }}
              />
            )}
          </BaseMap>
        </div>
      </div>

      {/* ── Right panel: Organisation + Category browser + Ref Image ── */}
      <div className="poi-right-panel">
        <Card bodyStyle={{ padding: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Organisation selector */}
          <div className="poi-org-selector">
            {isSuperAdmin ? (
              <>
                <label className="poi-org-label">Organisation(s)</label>
                <Select
                  mode="multiple"
                  value={selectedOrganisations}
                  onChange={(vals) => setSelectedOrganisations(vals || [])}
                  placeholder="Select organisation(s)"
                  optionFilterProp="children"
                  showSearch
                  maxTagCount={2}
                  size="small"
                  style={{ width: '100%' }}
                >
                  {organisations.map((org) => (
                    <Option key={org._id} value={String(org._id)}>{org.name}</Option>
                  ))}
                </Select>
              </>
            ) : (
              <div className="poi-org-badge">
                <Tag color="blue">{loggedUserOrganisation.name || 'Your Organisation'}</Tag>
              </div>
            )}
          </div>

          {/* Category list — scrollable middle */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <CategoryPanel
              allPois={allPois}
              hiddenCategories={hiddenCategories}
              selectedPoiId={selectedExistingFenceId || (editingExistingFence && editingExistingFence._id) || null}
              onToggleCategory={handleToggleCategory}
              onPoiSelect={handlePoiSelect}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          </div>

          {/* Reference image — pinned to bottom */}
          <div className="poi-ref-image-footer">
            <RefImagePanel
              refImage={refImage}
              imageOpacity={imageOpacity}
              tiffLoading={tiffLoading}
              onImageUpload={handleImageUpload}
              onOpacityChange={setImageOpacity}
              onResetToView={handleResetToView}
              onClearImage={handleClearImage}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
